import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Alert, InputAdornment,
  Chip, CircularProgress, IconButton, Divider,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, FormGroup, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton, ListItemText,
} from '@mui/material';
import {
  Save, ArrowBack, CheckCircle, Search, History, AddCircleOutline, Delete, Business,
} from '@mui/icons-material';
import { ordersAPI, customersAdminAPI } from '../services/api';
import { estimateMinimumCost } from '../utils/costEstimator';

const DISPATCH_TYPES = [
  { key: 'urgent',     label: '긴급' },
  { key: 'round_trip', label: '왕복' },
  { key: 'waypoint',   label: '경유' },
];
const DISPATCH_LABEL = Object.fromEntries(DISPATCH_TYPES.map((d) => [d.key, d.label]));
const HANDLING_METHODS = ['지게차', '호이스트', '인력 제공', '수작업', '크레인'];
const VEHICLE_TYPES = [
  '차종 무관', '카고', '카고/탑', '카고리프트', '윙바디', '윙바디/탑', '윙+', '탑',
  '탑리프트', '리프트', '리프트윙', '호로리프트', '호로', '추레라', '다마스', '라보',
  '무진동', '초장축', '냉장탑', '냉동탑', '냉장윙', '냉동탑리프트', '냉장윙리프트',
];
const WEIGHT_OPTIONS = [
  '0.3t', '0.5t', '1t', '1t초장축', '1.4t', '1.4t초장축', '2.5t', '3.5t', '3.5t광폭',
  '4t', '5t', '5플러스', '5t축', '5t플축', '8t', '9.5t', '11t', '14t', '16t', '18t',
  '22t', '25t',
];
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = [0, 10, 20, 30, 40, 50];

const PRICE_STATUS_OPTIONS = [
  { value: 'payment_pending',   label: '결재대기' },
  { value: 'payment_completed', label: '결재완료 (입금처리)' },
  { value: 'deferred_payment',  label: '추후결재' },
];

function calcPresetDate(preset) {
  const now = new Date();
  if (preset === 'now')      return now;
  if (preset === 'today')    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'tomorrow') { const d = new Date(now); d.setDate(d.getDate() + 1); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  if (preset === 'monday')   {
    const d = new Date(now);
    const day = d.getDay();
    const daysUntilMon = (day === 1) ? 7 : ((8 - day) % 7) || 7;
    d.setDate(d.getDate() + daysUntilMon);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
}
function combineDateTime(baseDate, ampm, hour, minute) {
  if (!baseDate) return null;
  let h = hour;
  if (ampm === 'AM') h = (hour === 12) ? 0 : hour;
  else h = (hour === 12) ? 12 : hour + 12;
  return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h, minute);
}
function toMysqlDt(d) {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}
const ymdOf = (d) => d
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : '';

const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};

// 주소가 길면 앞 N자만 보여줌
const trimAddr = (s, n = 22) => {
  if (!s) return '';
  const str = String(s);
  return str.length <= n ? str : `${str.slice(0, n)}…`;
};

function DateTimePresetField({ title, presets, value, onChange, bgcolor }) {
  const autoDate = calcPresetDate(value.preset);
  const handlePresetChange = (next) => {
    const auto = next === 'now' ? new Date() : calcPresetDate(next);
    if (!auto) return onChange({ ...value, preset: next, customDate: '' });
    const ymd = ymdOf(auto);
    if (next === 'now') {
      const h24 = auto.getHours();
      const ampm = h24 < 12 ? 'AM' : 'PM';
      let hour = h24 % 12; if (hour === 0) hour = 12;
      const minute = (Math.round(auto.getMinutes() / 10) * 10) % 60;
      onChange({ ...value, preset: next, customDate: ymd, ampm, hour, minute });
    } else {
      onChange({ ...value, preset: next, customDate: ymd });
    }
  };
  return (
    <Card sx={{ mb: 2, borderRadius: 2, bgcolor }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>{title}</Typography>
        <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup size="small" color="primary" exclusive value={value.preset}
            onChange={(_, v) => v && handlePresetChange(v)} sx={{ flexWrap: 'wrap' }}>
            {presets.map((p) => (
              <ToggleButton key={p.key} value={p.key} sx={{ px: 2 }}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField type="date" size="small"
            value={value.customDate || ymdOf(autoDate)}
            onChange={(e) => onChange({ ...value, customDate: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
        </Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <ToggleButtonGroup size="small" exclusive value={value.ampm}
              onChange={(_, v) => v && onChange({ ...value, ampm: v })}>
              <ToggleButton value="AM">오전</ToggleButton>
              <ToggleButton value="PM">오후</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>시</InputLabel>
              <Select label="시" value={value.hour} onChange={(e) => onChange({ ...value, hour: e.target.value })}>
                {HOURS.map((h) => <MenuItem key={h} value={h}>{h}시</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>분</InputLabel>
              <Select label="분" value={value.minute} onChange={(e) => onChange({ ...value, minute: e.target.value })}>
                {MINUTES.map((m) => <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}분</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function PreviousOrdersDialog({ open, onClose, onPick, customerId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    customersAdminAPI.getOrders(customerId)
      .then((res) => setOrders((res.data?.data || []).slice(0, 20)))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [open, customerId]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>이 거래처의 이전 주문에서 불러오기</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : orders.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>이전 주문이 없습니다.</Box>
        ) : (
          <List disablePadding>
            {orders.map((o) => (
              <React.Fragment key={o.order_id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { onPick(o); onClose(); }}>
                    <ListItemText
                      primary={`${o.order_number} · ${o.created_at ? new Date(o.created_at).toLocaleDateString('ko-KR') : ''}`}
                      secondary={`${o.origin_address || '-'} → ${o.destination_address || '-'}`}
                      primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button></DialogActions>
    </Dialog>
  );
}

// 이전 거래에서 가져오기 — 거래처 + 마지막 주문 요약 (검색어 없어도 최근 50건)
function AllOrdersDialog({ open, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setLoading(false); }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const tid = setTimeout(async () => {
      try {
        const res = await customersAdminAPI.search(q.trim());
        setResults(res.data?.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, q ? 250 : 0);
    return () => clearTimeout(tid);
  }, [q, open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>이전 거래에서 가져오기</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <TextField fullWidth size="small" placeholder="업체명 / 담당자 / 전화 / 주소 검색 (비워두면 최근 거래 표시)"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            autoFocus />
        </Box>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : results.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>거래 내역이 없습니다.</Box>
        ) : (
          <List disablePadding sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {results.map((c) => {
              let dispatchTypes = [];
              try {
                const dt = c.dispatch_types;
                dispatchTypes = Array.isArray(dt) ? dt : (typeof dt === 'string' && dt ? JSON.parse(dt) : []);
              } catch { dispatchTypes = []; }
              const phone = c.phone && !String(c.phone).startsWith('MIG-') && !String(c.phone).startsWith('BN-') && !String(c.phone).startsWith('TMP-')
                ? c.phone : (c.office_phone || '');
              return (
                <React.Fragment key={`${c.customer_id}-${c.last_order_id || 'no_order'}`}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => { onPick(c); onClose(); }} sx={{ py: 1.2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography fontWeight={700} color="primary">
                            {c.company_name || c.name || '(이름없음)'}
                          </Typography>
                          {c.name && c.name !== c.company_name && (
                            <Typography variant="body2" color="text.secondary">담당자: {c.name}</Typography>
                          )}
                          {phone && <Chip label={phone} size="small" />}
                          {dispatchTypes.map((k) => DISPATCH_LABEL[k] && (
                            <Chip key={k} size="small" color={k === 'urgent' ? 'error' : (k === 'round_trip' ? 'info' : 'warning')}
                              label={`[${DISPATCH_LABEL[k]}]`} sx={{ height: 20, fontSize: 11 }} />
                          ))}
                        </Box>
                        {(c.origin_address || c.destination_address) ? (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', color: 'text.secondary', fontSize: 13, alignItems: 'center' }}>
                            {c.last_order_at && (
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                {new Date(c.last_order_at).toLocaleDateString('ko-KR')}
                              </Typography>
                            )}
                            {c.origin_address && (
                              <Typography variant="caption">
                                <strong style={{ color: '#2e7d32' }}>상차</strong> {trimAddr(c.origin_address, 26)}
                              </Typography>
                            )}
                            {c.destination_address && (
                              <Typography variant="caption">
                                <strong style={{ color: '#c62828' }}>하차</strong> {trimAddr(c.destination_address, 26)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {c.address ? trimAddr(c.address, 40) : '거래 내역 없음 — 거래처 정보만 가져옵니다.'}
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button></DialogActions>
    </Dialog>
  );
}

const parseJsonArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
};

function dtToPresetValue(dtStr) {
  if (!dtStr) return null;
  const d = new Date(dtStr);
  if (isNaN(d.getTime())) return null;
  const h24 = d.getHours();
  const ampm = h24 < 12 ? 'AM' : 'PM';
  let hour = h24 % 12; if (hour === 0) hour = 12;
  const minute = (Math.round(d.getMinutes() / 10) * 10) % 60;
  return { preset: 'today', customDate: ymdOf(d), ampm, hour, minute };
}

export default function CreateOrder() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);

  const [customerForm, setCustomerForm] = useState({
    company_name: '', business_number: '', ceo_name: '',
    name: '', phone: '', email: '', office_phone: '', address: '',
    settlement_method: '',
  });
  const [resolvedCustomer, setResolvedCustomer] = useState(null);
  const [bnLookupLoading, setBnLookupLoading] = useState(false);

  const [prevDialogOpen, setPrevDialogOpen] = useState(false);
  const [allOrdersOpen, setAllOrdersOpen] = useState(false);

  // 최소경비 계산
  const [costEstimate, setCostEstimate] = useState(null);
  const [costEstimating, setCostEstimating] = useState(false);
  const [costEstimateError, setCostEstimateError] = useState('');
  const [form, setForm] = useState({
    dispatch_types: [],
    waypoints: [],
    origin_address: '', origin_contact_name: '', origin_contact: '',
    destination_address: '', destination_contact_name: '', destination_contact: '',
    pickup:   { preset: 'today', ampm: 'AM', hour: 9, minute: 0 },
    delivery: { preset: 'today', ampm: 'PM', hour: 6, minute: 0 },
    loading_methods: [], unloading_methods: [],
    vehicle_types: [], vehicle_weights: [],
    client_offered_price: '',
    confirmed_price: '',
    special_notes: '',
    price_status: 'payment_pending',
  });
  const isWaypoint = form.dispatch_types.includes('waypoint');

  // 업체명 Autocomplete 옵션 (거래처 목록 — customer_id 단위 dedupe)
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const searchCustomers = async (q) => {
    setCustomerSearchLoading(true);
    try {
      const res = await customersAdminAPI.search(q || '');
      const rows = res.data?.data || [];
      // customer_id 중복 제거 (search/quick 은 주문 단위 row 를 반환)
      const seen = new Set();
      const unique = [];
      for (const r of rows) {
        if (seen.has(r.customer_id)) continue;
        seen.add(r.customer_id);
        unique.push(r);
      }
      setCustomerOptions(unique);
    } catch { setCustomerOptions([]); }
    finally { setCustomerSearchLoading(false); }
  };
  // 마운트 시 최근 거래처 미리 로드 + 입력 변경 시 debounced 재검색
  useEffect(() => { searchCustomers(''); }, []);
  const debounceRef = useRef(null);
  const handleCustomerInput = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { searchCustomers(text); }, 250);
  };

  // 거래처 정보 가져오기 체크박스 상태 (상차/하차/경유지[i])
  const [sameAsCustomerOrigin, setSameAsCustomerOrigin] = useState(false);
  const [sameAsCustomerDest, setSameAsCustomerDest] = useState(false);
  const [sameAsCustomerWp, setSameAsCustomerWp] = useState({});

  const cleanPhone = (s) => {
    if (!s) return '';
    const isPlaceholder = String(s).startsWith('BN-') || String(s).startsWith('MIG-') || String(s).startsWith('TMP-');
    return isPlaceholder ? '' : s;
  };
  const handleCopyCustomerToOrigin = (checked) => {
    setSameAsCustomerOrigin(checked);
    const phone = cleanPhone(customerForm.phone) || customerForm.office_phone || '';
    setForm((p) => ({
      ...p,
      origin_address:      checked ? (customerForm.address || '') : '',
      origin_contact_name: checked ? (customerForm.name || '')    : '',
      origin_contact:      checked ? formatPhone(phone)           : '',
    }));
  };
  const handleCopyCustomerToDest = (checked) => {
    setSameAsCustomerDest(checked);
    const phone = cleanPhone(customerForm.phone) || customerForm.office_phone || '';
    setForm((p) => ({
      ...p,
      destination_address:      checked ? (customerForm.address || '') : '',
      destination_contact_name: checked ? (customerForm.name || '')    : '',
      destination_contact:      checked ? formatPhone(phone)           : '',
    }));
  };
  const handleCopyCustomerToWp = (idx, checked) => {
    setSameAsCustomerWp((s) => ({ ...s, [idx]: checked }));
    const phone = cleanPhone(customerForm.phone) || customerForm.office_phone || '';
    setForm((p) => {
      const next = [...p.waypoints];
      next[idx] = {
        ...next[idx],
        address:      checked ? (customerForm.address || '') : '',
        contact_name: checked ? (customerForm.name || '')    : '',
        contact:      checked ? formatPhone(phone)           : '',
      };
      return { ...p, waypoints: next };
    });
  };

  const lookupByBn = async () => {
    const bn = customerForm.business_number.trim();
    if (!bn) return;
    setBnLookupLoading(true);
    try {
      const res = await customersAdminAPI.lookupByBusinessNumber(bn);
      const found = res.data?.data;
      if (found) {
        setResolvedCustomer(found);
        setCustomerForm({
          company_name: found.company_name || '',
          business_number: found.business_number || '',
          ceo_name: found.ceo_name || '',
          name: found.name && !String(found.name).startsWith('BN-') ? found.name : '',
          phone: formatPhone(found.phone && !String(found.phone).startsWith('MIG-') && !String(found.phone).startsWith('BN-') && !String(found.phone).startsWith('TMP-') ? found.phone : ''),
          email: found.email || '',
          office_phone: formatPhone(found.office_phone || ''),
          address: found.address || '',
          settlement_method: found.settlement_method || '',
        });
      } else {
        setResolvedCustomer(null);
      }
    } catch {
      setResolvedCustomer(null);
    } finally {
      setBnLookupLoading(false);
    }
  };

  const toggleListItem = (field, value) => {
    setForm((p) => ({
      ...p,
      [field]: p[field].includes(value) ? p[field].filter((x) => x !== value) : [...p[field], value],
    }));
  };
  const handleDispatchToggle = (key) => {
    setForm((p) => {
      const next = p.dispatch_types.includes(key) ? p.dispatch_types.filter((x) => x !== key) : [...p.dispatch_types, key];
      let waypoints = p.waypoints;
      if (key === 'waypoint' && !next.includes('waypoint')) waypoints = [];
      if (key === 'waypoint' && next.includes('waypoint') && waypoints.length === 0) waypoints = [{ address: '', contact_name: '', contact: '' }];
      return { ...p, dispatch_types: next, waypoints };
    });
  };
  const openAddressSearch = (cb) => {
    if (!window.daum || !window.daum.Postcode) return alert('주소 검색 서비스를 불러오는 중입니다.');
    new window.daum.Postcode({
      oncomplete: (data) => cb(data.roadAddress || data.jibunAddress),
    }).open();
  };

  // 최소경비 산출
  const handleEstimateCost = async () => {
    if (!form.origin_address || !form.destination_address) {
      setCostEstimateError('상차지와 하차지 주소를 먼저 입력하세요.');
      setCostEstimate(null);
      return;
    }
    setCostEstimating(true);
    setCostEstimateError('');
    setCostEstimate(null);
    try {
      const wpAddresses = (form.waypoints || []).map((w) => w?.address).filter(Boolean);
      const result = await estimateMinimumCost({
        originAddress: form.origin_address,
        destinationAddress: form.destination_address,
        vehicleType: form.vehicle_types?.[0] || null,
        waypointAddresses: wpAddresses,
      });
      setCostEstimate(result);
    } catch (err) {
      setCostEstimateError(err.message || '경비 산출에 실패했습니다.');
    } finally {
      setCostEstimating(false);
    }
  };
  const applyEstimatedTo = (field) => {
    if (costEstimate?.recommendedPrice) {
      setForm((p) => ({ ...p, [field]: String(costEstimate.recommendedPrice) }));
    }
  };
  const handlePickPrevious = (o) => {
    const waypoints = parseJsonArr(o.waypoints);
    const vehicleTypes = parseJsonArr(o.vehicle_types);
    const vehicleWeights = parseJsonArr(o.vehicle_weights);
    const loadingMethods = parseJsonArr(o.loading_methods);
    const unloadingMethods = parseJsonArr(o.unloading_methods);
    const dispatchTypes = parseJsonArr(o.dispatch_types);
    // 경유지 유무에 맞춰 dispatch_types 동기화
    const baseDispatch = dispatchTypes.filter((k) => k !== 'waypoint');
    const finalDispatch = waypoints.length > 0
      ? Array.from(new Set([...baseDispatch, 'waypoint']))
      : baseDispatch;
    setForm((p) => ({
      ...p,
      origin_address:           o.origin_address || '',
      origin_contact:           formatPhone(o.origin_contact || ''),
      origin_contact_name:      o.origin_contact_name || '',
      destination_address:      o.destination_address || '',
      destination_contact:      formatPhone(o.destination_contact || ''),
      destination_contact_name: o.destination_contact_name || '',
      vehicle_types:            vehicleTypes,
      vehicle_weights:          vehicleWeights,
      loading_methods:          loadingMethods,
      unloading_methods:        unloadingMethods,
      dispatch_types:           finalDispatch,
      waypoints:                waypoints,
      client_offered_price:     o.client_offered_price ? String(parseInt(o.client_offered_price, 10)) : '',
      confirmed_price:          o.confirmed_price      ? String(parseInt(o.confirmed_price, 10))      : '',
      special_notes: o.special_notes || '',
    }));
  };

  // 이전 거래 row 선택 → 거래처 + 그 행의 주문 정보를 한 페이지에 모두 채움
  const handlePickAllOrder = (c) => {
    setError('');
    // 거래처 정보 채우기 — 라벨에 맞춘 매핑
    setResolvedCustomer(c);
    setCustomerForm({
      company_name: c.company_name || '',
      business_number: c.business_number || '',
      ceo_name: c.ceo_name || '',
      name: c.name && !String(c.name).startsWith('BN-') ? c.name : '',
      phone: formatPhone(c.phone && !String(c.phone).startsWith('MIG-') && !String(c.phone).startsWith('BN-') && !String(c.phone).startsWith('TMP-') ? c.phone : ''),
      email: c.email || '',
      office_phone: formatPhone(c.office_phone || ''),
      address: c.address || '',
      settlement_method: c.settlement_method || '',
    });

    // 선택한 행에 주문 정보가 함께 들어 있으면 운송 정보까지 모두 채움
    // (검색 API 가 주문 컬럼들을 그대로 반환하므로 별도 fetch 불필요)
    if (c.last_order_id) {
      handlePickPrevious(c);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!customerForm.company_name.trim()) return setError('업체명을 입력하세요.');
    if (!form.origin_address) return setError('상차지 주소를 입력하세요.');
    if (!form.destination_address) return setError('하차지 주소를 입력하세요.');
    if (isWaypoint && form.waypoints.some((w) => !w.address)) return setError('경유지 주소를 모두 입력하세요.');

    const resolveBase = (v) => v.customDate ? new Date(v.customDate + 'T00:00:00') : calcPresetDate(v.preset);
    const pickupDt   = combineDateTime(resolveBase(form.pickup), form.pickup.ampm, form.pickup.hour, form.pickup.minute);
    const deliveryDt = combineDateTime(resolveBase(form.delivery), form.delivery.ampm, form.delivery.hour, form.delivery.minute);
    if (pickupDt && deliveryDt && deliveryDt < pickupDt) return setError('하차 일시가 상차 일시보다 빠릅니다.');

    setLoading(true);
    try {
      // 1) 거래처 find-or-create
      const custRes = await customersAdminAPI.findOrCreate(customerForm);
      const customer = custRes.data?.data;
      if (!customer || !customer.customer_id) {
        throw new Error('거래처 처리에 실패했습니다.');
      }
      setResolvedCustomer(customer);

      // 2) 주문 생성
      const payload = {
        customer_id: customer.customer_id,
        origin_address: form.origin_address,
        origin_contact: form.origin_contact || null,
        origin_contact_name: form.origin_contact_name || null,
        destination_address: form.destination_address,
        destination_contact: form.destination_contact || null,
        destination_contact_name: form.destination_contact_name || null,
        pickup_datetime: pickupDt ? toMysqlDt(pickupDt) : null,
        desired_delivery_datetime: deliveryDt ? toMysqlDt(deliveryDt) : null,
        client_offered_price: form.client_offered_price ? parseInt(form.client_offered_price, 10) : null,
        confirmed_price: form.confirmed_price ? parseInt(form.confirmed_price, 10) : null,
        required_vehicle_type: form.vehicle_types[0] || null,
        required_weight: form.vehicle_weights[0] ? String(form.vehicle_weights[0]).replace(/[^\d.]/g, '') : null,
        vehicle_types: form.vehicle_types,
        vehicle_weights: form.vehicle_weights,
        dispatch_types: form.dispatch_types,
        waypoints: form.waypoints,
        loading_methods: form.loading_methods,
        unloading_methods: form.unloading_methods,
        special_notes: form.special_notes || null,
        price_status: form.price_status,
      };
      const res = await ordersAPI.create(payload);
      setCreatedOrder(res.data?.data || {});
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || '주문 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted && createdOrder) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CheckCircle sx={{ fontSize: 72, color: '#2e7d32', mb: 2 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>주문 생성 완료</Typography>
        <Typography color="text.secondary" gutterBottom>주문번호: <strong>{createdOrder.order_number}</strong></Typography>
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => window.location.reload()}>새 주문 작성</Button>
          <Button variant="contained" onClick={() => navigate('/orders')}>주문 목록</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>주문 생성</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="success" startIcon={<History />} onClick={() => setAllOrdersOpen(true)}>
            이전 거래에서 가져오기
          </Button>
          {resolvedCustomer?.customer_id && (
            <Button variant="outlined" startIcon={<History />} onClick={() => setPrevDialogOpen(true)}>
              이 거래처의 이전 주문
            </Button>
          )}
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/orders')}>목록으로</Button>
        </Box>
      </Box>

      <AllOrdersDialog open={allOrdersOpen} onClose={() => setAllOrdersOpen(false)} onPick={handlePickAllOrder} />
      <PreviousOrdersDialog open={prevDialogOpen} onClose={() => setPrevDialogOpen(false)}
        onPick={handlePickPrevious} customerId={resolvedCustomer?.customer_id} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* 거래처 정보 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Business color="primary" />
              <Typography variant="h6" fontWeight={700}>거래처 정보</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={customerOptions}
                  loading={customerSearchLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt?.company_name || opt?.name || '')}
                  isOptionEqualToValue={(opt, v) => opt?.customer_id === v?.customer_id}
                  value={customerForm.company_name}
                  inputValue={customerForm.company_name}
                  onInputChange={(_, v, reason) => {
                    if (reason === 'reset') return;
                    setCustomerForm((p) => ({ ...p, company_name: v }));
                    handleCustomerInput(v);
                  }}
                  onChange={(_, selected) => {
                    if (selected && typeof selected === 'object' && selected.customer_id) {
                      handlePickAllOrder(selected);
                    }
                  }}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt.customer_id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography fontWeight={700} color="primary">
                            {opt.company_name || opt.name || '(이름없음)'}
                          </Typography>
                          {opt.name && opt.name !== opt.company_name && (
                            <Typography variant="caption" color="text.secondary">담당자: {opt.name}</Typography>
                          )}
                          {opt.business_number && (
                            <Typography variant="caption" color="text.secondary">· {opt.business_number}</Typography>
                          )}
                        </Box>
                        {opt.address && (
                          <Typography variant="caption" color="text.secondary">
                            {String(opt.address).slice(0, 40)}{String(opt.address).length > 40 ? '…' : ''}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} required label="업체명" placeholder="입력하거나 펼침 화살표로 선택" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="사업자등록번호 (선택)"
                  value={customerForm.business_number}
                  onChange={(e) => {
                    const d = (e.target.value || '').replace(/\D/g, '').slice(0, 10);
                    const v = d.length < 4 ? d
                      : d.length < 6 ? `${d.slice(0,3)}-${d.slice(3)}`
                      : `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,10)}`;
                    setCustomerForm({ ...customerForm, business_number: v });
                  }}
                  inputProps={{ maxLength: 12 }}
                  onBlur={lookupByBn}
                  placeholder="000-00-00000"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" onClick={lookupByBn} disabled={bnLookupLoading}>
                          {bnLookupLoading ? <CircularProgress size={16} /> : '조회'}
                        </Button>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              {resolvedCustomer && (
                <Grid item xs={12}>
                  <Alert severity="success" icon={<Business />}>
                    <strong>기존 거래처</strong> — {resolvedCustomer.company_name}
                    {resolvedCustomer.business_number ? ` (${resolvedCustomer.business_number})` : ''}
                    {resolvedCustomer.ceo_name ? ` · 대표 ${resolvedCustomer.ceo_name}` : ''}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="대표자명" value={customerForm.ceo_name}
                  onChange={(e) => setCustomerForm({ ...customerForm, ceo_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={customerOptions.filter((o) => o.name && o.name.trim() !== '')}
                  loading={customerSearchLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt?.name || '')}
                  isOptionEqualToValue={(opt, v) => opt?.customer_id === v?.customer_id}
                  value={customerForm.name}
                  inputValue={customerForm.name}
                  onInputChange={(_, v, reason) => {
                    if (reason === 'reset') return;
                    setCustomerForm((p) => ({ ...p, name: v }));
                    handleCustomerInput(v);
                  }}
                  onChange={(_, selected) => {
                    if (selected && typeof selected === 'object' && selected.customer_id) {
                      handlePickAllOrder(selected);
                    }
                  }}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt.customer_id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography fontWeight={700} color="primary">{opt.name}</Typography>
                          {opt.company_name && opt.company_name !== opt.name && (
                            <Typography variant="caption" color="text.secondary">{opt.company_name}</Typography>
                          )}
                          {opt.phone && !String(opt.phone).startsWith('BN-') && !String(opt.phone).startsWith('MIG-') && !String(opt.phone).startsWith('TMP-') && (
                            <Typography variant="caption" color="text.secondary">· {opt.phone}</Typography>
                          )}
                        </Box>
                        {opt.address && (
                          <Typography variant="caption" color="text.secondary">
                            {String(opt.address).slice(0, 40)}{String(opt.address).length > 40 ? '…' : ''}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="담당자" placeholder="입력하거나 펼침 화살표로 선택" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="E-Mail" type="email" value={customerForm.email}
                  onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                  placeholder="예: contact@company.co.kr" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="휴대폰 번호" value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: formatPhone(e.target.value) })}
                  placeholder="010-1234-5678" />
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField fullWidth label="주소" value={customerForm.address}
                  onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" variant="outlined" startIcon={<Search />}
                          onClick={() => openAddressSearch((addr) => setCustomerForm((p) => ({ ...p, address: addr })))}>검색</Button>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>결재</InputLabel>
                  <Select label="결재" value={customerForm.settlement_method || ''}
                    onChange={(e) => setCustomerForm({ ...customerForm, settlement_method: e.target.value })}>
                    <MenuItem value=""><em>(없음)</em></MenuItem>
                    <MenuItem value="신용">신용</MenuItem>
                    <MenuItem value="송금">송금</MenuItem>
                    <MenuItem value="착불">착불</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 구분 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>구분</Typography>
            <FormGroup row>
              {DISPATCH_TYPES.map((d) => (
                <FormControlLabel key={d.key}
                  control={<Checkbox checked={form.dispatch_types.includes(d.key)} onChange={() => handleDispatchToggle(d.key)} />}
                  label={d.label} />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        {/* 상차지 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>상차지 정보</Typography>
              <FormControlLabel
                control={<Checkbox checked={sameAsCustomerOrigin} onChange={(e) => handleCopyCustomerToOrigin(e.target.checked)} />}
                label="거래처 정보 가져오기"
                sx={{ m: 0 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth required label="상차지 주소 *" value={form.origin_address}
                  onChange={(e) => setForm({ ...form, origin_address: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" variant="outlined" startIcon={<Search />}
                          onClick={() => openAddressSearch((addr) => setForm((p) => ({ ...p, origin_address: addr })))}>검색</Button>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="상차지 담당자" value={form.origin_contact_name}
                  onChange={(e) => setForm({ ...form, origin_contact_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="상차지 연락처" value={form.origin_contact}
                  onChange={(e) => setForm({ ...form, origin_contact: formatPhone(e.target.value) })}
                  placeholder="010-1234-5678" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 경유지 */}
        {isWaypoint && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6" fontWeight={600}>경유지</Typography>
                <Button size="small" startIcon={<AddCircleOutline />}
                  onClick={() => setForm({ ...form, waypoints: [...form.waypoints, { address: '', contact_name: '', contact: '' }] })}>경유지 추가</Button>
              </Box>
              {form.waypoints.map((wp, i) => (
                <Box key={i} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!sameAsCustomerWp[i]} onChange={(e) => handleCopyCustomerToWp(i, e.target.checked)} />}
                      label={<Typography variant="caption">거래처 정보 가져오기</Typography>}
                      sx={{ m: 0 }}
                    />
                  </Box>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs>
                      <TextField fullWidth size="small" label={`경유지 ${i + 1} 주소 *`} value={wp.address || ''}
                        onChange={(e) => {
                          const next = [...form.waypoints]; next[i] = { ...next[i], address: e.target.value };
                          setForm({ ...form, waypoints: next });
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button size="small" variant="outlined" startIcon={<Search />}
                                onClick={() => openAddressSearch((addr) => {
                                  const next = [...form.waypoints]; next[i] = { ...next[i], address: addr };
                                  setForm({ ...form, waypoints: next });
                                })}>검색</Button>
                            </InputAdornment>
                          ),
                        }} />
                    </Grid>
                    <Grid item>
                      <IconButton color="error" onClick={() => setForm({ ...form, waypoints: form.waypoints.filter((_, idx) => idx !== i) })}>
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="담당자" value={wp.contact_name || ''}
                        onChange={(e) => {
                          const next = [...form.waypoints]; next[i] = { ...next[i], contact_name: e.target.value };
                          setForm({ ...form, waypoints: next });
                        }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="연락처" value={wp.contact || ''}
                        onChange={(e) => {
                          const next = [...form.waypoints]; next[i] = { ...next[i], contact: e.target.value };
                          setForm({ ...form, waypoints: next });
                        }}
                        placeholder="010-1234-5678" />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* 하차지 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>하차지 정보</Typography>
              <FormControlLabel
                control={<Checkbox checked={sameAsCustomerDest} onChange={(e) => handleCopyCustomerToDest(e.target.checked)} />}
                label="거래처 정보 가져오기"
                sx={{ m: 0 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth required label="하차지 주소 *" value={form.destination_address}
                  onChange={(e) => setForm({ ...form, destination_address: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" variant="outlined" startIcon={<Search />}
                          onClick={() => openAddressSearch((addr) => setForm((p) => ({ ...p, destination_address: addr })))}>검색</Button>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="하차지 담당자" value={form.destination_contact_name}
                  onChange={(e) => setForm({ ...form, destination_contact_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="하차지 연락처" value={form.destination_contact}
                  onChange={(e) => setForm({ ...form, destination_contact: formatPhone(e.target.value) })}
                  placeholder="010-9876-5432" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <DateTimePresetField
          title="상차일시"
          presets={[
            { key: 'now', label: '지금' }, { key: 'today', label: '당일 상차' },
            { key: 'tomorrow', label: '내일 상차' }, { key: 'monday', label: '월요일 상차' },
          ]}
          value={form.pickup} onChange={(v) => setForm({ ...form, pickup: v })} bgcolor="#f1f8e9" />

        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>상차 방법</Typography>
            <FormGroup row>
              {HANDLING_METHODS.map((m) => (
                <FormControlLabel key={m}
                  control={<Checkbox checked={form.loading_methods.includes(m)} onChange={() => toggleListItem('loading_methods', m)} />}
                  label={m} />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        <DateTimePresetField
          title="하차일시"
          presets={[
            { key: 'today', label: '당일 하차' }, { key: 'tomorrow', label: '내일 하차' },
            { key: 'monday', label: '월요일 하차' },
          ]}
          value={form.delivery} onChange={(v) => setForm({ ...form, delivery: v })} bgcolor="#fff8e1" />

        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>하차 방법</Typography>
            <FormGroup row>
              {HANDLING_METHODS.map((m) => (
                <FormControlLabel key={m}
                  control={<Checkbox checked={form.unloading_methods.includes(m)} onChange={() => toggleListItem('unloading_methods', m)} />}
                  label={m} />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  차종 <Typography component="span" variant="caption" color="text.secondary">(최대 3개)</Typography>
                </Typography>
                <Autocomplete multiple options={VEHICLE_TYPES} value={form.vehicle_types}
                  onChange={(_, v) => { if (v.length <= 3) setForm({ ...form, vehicle_types: v }); }}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => <Chip key={opt} label={opt} color="primary" size="small" {...getTagProps({ index: idx })} />)
                  }
                  renderInput={(params) => <TextField {...params} placeholder="차종 선택" />} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  톤수 <Typography component="span" variant="caption" color="text.secondary">(최대 3개)</Typography>
                </Typography>
                <Autocomplete multiple options={WEIGHT_OPTIONS} value={form.vehicle_weights}
                  onChange={(_, v) => { if (v.length <= 3) setForm({ ...form, vehicle_weights: v }); }}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => <Chip key={opt} label={opt} color="secondary" size="small" {...getTagProps({ index: idx })} />)
                  }
                  renderInput={(params) => <TextField {...params} placeholder="톤수 선택" />} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>금액 정보 (선택)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="제시금액 (고객)"
                  value={form.client_offered_price ? Number(form.client_offered_price).toLocaleString() : ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setForm({ ...form, client_offered_price: v });
                  }}
                  InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff3e0' } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="배차금액 (기사 지급)"
                  value={form.confirmed_price ? Number(form.confirmed_price).toLocaleString() : ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setForm({ ...form, confirmed_price: v });
                  }}
                  InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }} />
              </Grid>
            </Grid>

            {/* 최소경비 산출 (계산하기) */}
            <Box sx={{ mt: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>🧮 예상 최소경비 산출</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleEstimateCost}
                  disabled={costEstimating}
                  startIcon={costEstimating ? <CircularProgress size={14} /> : null}
                >
                  {costEstimate ? '다시 계산' : '계산하기'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                주소·차종({form.vehicle_types?.[0] || '미지정'}) 기반 추정치 — 직선/운행거리, 주유비, 톨비, 인건비, 감가상각비 포함 (왕복 기준)
              </Typography>

              {costEstimateError && (
                <Alert severity="error" sx={{ mt: 1 }}>{costEstimateError}</Alert>
              )}

              {costEstimate && (
                <Box sx={{ mt: 1.5 }}>
                  <Grid container spacing={0.5}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">차량 (가정)</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption">{costEstimate.vehicleLabel}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">직선 거리</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption">{costEstimate.straightKm} km</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">운행 거리 (편도 / 왕복)</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption">{costEstimate.onewayKm} km / {costEstimate.totalKm} km</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">예상 시간 (운전 + 상하차)</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption">{costEstimate.totalHours} 시간</Typography></Grid>
                  </Grid>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={0.5}>
                    <Grid item xs={7}><Typography variant="body2">⛽ 주유비 ({costEstimate.fuelLiters} L)</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.fuelCost.toLocaleString()}원</Typography></Grid>
                    <Grid item xs={7}><Typography variant="body2">🛣️ 톨비 (고속 {costEstimate.tollKm} km)</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.tollCost.toLocaleString()}원</Typography></Grid>
                    <Grid item xs={7}><Typography variant="body2">👤 인건비</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.laborCost.toLocaleString()}원</Typography></Grid>
                    <Grid item xs={7}><Typography variant="body2">🚛 차량 감가/유지비</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.depreciationCost.toLocaleString()}원</Typography></Grid>
                  </Grid>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={0.5}>
                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">소계</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2" color="text.secondary">{costEstimate.subtotal.toLocaleString()}원</Typography></Grid>
                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">마진 ({Math.round(costEstimate.marginRate * 100)}%)</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2" color="text.secondary">{costEstimate.margin.toLocaleString()}원</Typography></Grid>
                    <Grid item xs={7}><Typography variant="subtitle2" fontWeight={700} color="primary">권장 최소금액</Typography></Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary">{costEstimate.recommendedPrice.toLocaleString()}원</Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => applyEstimatedTo('client_offered_price')}>
                      제시금액으로 입력
                    </Button>
                    <Button size="small" variant="contained" color="primary" onClick={() => applyEstimatedTo('confirmed_price')}>
                      배차금액으로 입력
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* 결재 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>결재</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>결재 상태</InputLabel>
              <Select label="결재 상태" value={form.price_status}
                onChange={(e) => setForm({ ...form, price_status: e.target.value })}>
                {PRICE_STATUS_OPTIONS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>특이사항</Typography>
            <TextField fullWidth multiline rows={3} value={form.special_notes}
              onChange={(e) => setForm({ ...form, special_notes: e.target.value })}
              placeholder="화물 종류, 주의사항, 요청사항 등" />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/orders')} disabled={loading}>취소</Button>
          <Button type="submit" variant="contained" size="large"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Save />} disabled={loading}>
            {loading ? '생성 중...' : '주문 생성'}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
